import React, { useState } from 'react';
import Popup from './Popup';

const OPT_COLOR = { Retire: '#CF222E', Refactor: '#9A6700', New: '#1A7F37', Replace: '#6639BA' };
const OPT_BG    = { Retire: '#FFEBE9', Refactor: '#FFF8C5', New: '#DAFBE1', Replace: '#FBEFFF' };
const OPT_ICON  = { Retire: '🗑️',     Refactor: '🔧',      New: '🆕',      Replace: '🛒' };

// ── Full AI scoring modal (shown when clicking "Scored by AI") ────────────────
function RationalizationModal({ lane, onClose }) {
  const scores = lane.scores || {};
  const recommendation = lane.recommendation || '';
  const maxScore = Math.max(...Object.values(scores).map(s => s?.score || 0), 0);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 3000,
      background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        width: '100%', maxWidth: 560, maxHeight: '88vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        animation: 'modal-in 0.18s ease',
      }}>
        <style>{`@keyframes modal-in { from { opacity:0; transform:translateY(-12px) scale(0.98); } to { opacity:1; transform:none; } }`}</style>

        {/* Header */}
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #D0D7DE',
          background: '#F6F8FA', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>🧠</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: '#1A1F2E' }}>AI Scoring — {lane.name}</div>
            <div style={{ fontSize: 11, color: '#57606A', marginTop: 1 }}>
              {lane.lang} · {lane.fw} · {lane.loc?.toLocaleString()} LOC
              {lane.uncertain && <span style={{ marginLeft: 8, color: '#9A6700', fontWeight: 700 }}>⚠ uncertain</span>}
            </div>
          </div>
          {lane.target_stack && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
              background: '#DDF4FF', color: '#0969DA' }}>
              → {lane.target_stack}
            </span>
          )}
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer',
            color: '#57606A', fontSize: 18, lineHeight: 1, padding: 0, marginLeft: 4 }}>×</button>
        </div>

        {/* Reasoning */}
        {lane.reasoning && (
          <div style={{ padding: '10px 20px', background: '#FFFBF0', borderBottom: '1px solid #D0D7DE',
            fontSize: 12, color: '#1A1F2E', lineHeight: 1.6 }}>
            {lane.reasoning}
          </div>
        )}

        {/* Scores */}
        <div style={{ overflowY: 'auto', padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {['Retire', 'Refactor', 'New', 'Replace'].map(opt => {
            const s        = scores[opt] || { score: 0, justification: '' };
            const isTop    = s.score === maxScore && s.score > 0;
            const isChosen = recommendation === opt;
            const optColor = OPT_COLOR[opt];
            const optBg    = OPT_BG[opt];
            const pct      = Math.round((s.score / 10) * 100);

            return (
              <div key={opt} style={{
                border: `1.5px solid ${isChosen ? optColor : isTop ? optColor + '88' : '#D0D7DE'}`,
                borderRadius: 8, overflow: 'hidden',
                background: isChosen ? optBg : isTop ? optBg + '55' : '#fff',
                boxShadow: isChosen ? `0 0 0 2px ${optColor}33` : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px' }}>
                  <span style={{ fontSize: 14, width: 18 }}>{OPT_ICON[opt]}</span>
                  <span style={{ fontSize: 12, fontWeight: isChosen || isTop ? 700 : 500,
                    color: isChosen ? optColor : isTop ? optColor : '#57606A', width: 64, flexShrink: 0 }}>
                    {opt}
                  </span>
                  <div style={{ flex: 1, height: 8, background: '#EFF2F5', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: optColor,
                      borderRadius: 4, transition: 'width 0.6s' }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: optColor, width: 30, textAlign: 'right', flexShrink: 0 }}>
                    {s.score?.toFixed(1)}
                  </span>
                  {isChosen && (
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 6,
                      background: optColor, color: '#fff', flexShrink: 0 }}>
                      AI PICK
                    </span>
                  )}
                  {!isChosen && isTop && (
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 6,
                      background: optColor, color: '#fff', flexShrink: 0 }}>
                      TOP
                    </span>
                  )}
                </div>
                {s.justification && (
                  <div style={{ padding: '0 12px 8px 42px', fontSize: 11,
                    color: isChosen || isTop ? '#1A1F2E' : '#57606A', lineHeight: 1.5 }}>
                    {s.justification}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 20px', borderTop: '1px solid #D0D7DE', background: '#F6F8FA',
          display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            padding: '6px 16px', borderRadius: 6, fontSize: 12, fontWeight: 700,
            background: '#0969DA', color: '#fff', border: 'none', cursor: 'pointer',
          }}>Close</button>
        </div>
      </div>
    </div>
  );
}

const STAGES = [
  {
    key:         'discover',
    label:       'App Discovered',
    icon:        '🔍',
    description: 'The application was scanned to identify its programming language, framework, file count, and known security or quality issues.',
    what:        'Automated portfolio analysis',
  },
  {
    key:         'rationalize',
    label:       'Scored by AI',
    icon:        '🧠',
    description: 'AI reviewed the application code and issues, then recommended the best modernization path — Retire, Refactor, or Rebuild — with a risk score and justification.',
    what:        'AI-powered modernization assessment',
  },
  {
    key:         'requirements',
    label:       'Requirements Set',
    icon:        '📝',
    description: 'AI drafted a full requirements document covering goals, technical specifications, API design, and acceptance criteria. Reviewed and approved by you before work begins.',
    what:        'AI drafting + human approval',
  },
  {
    key:         'coding',
    label:       'Code Written',
    icon:        '💻',
    description: 'AI generated the complete modernized application code and a full test suite. Output is saved to the generated-apps folder.',
    what:        'AI code generation',
  },
  {
    key:         'testing',
    label:       'Tests Run',
    icon:        '🧪',
    description: 'Automated tests were run on the generated application. Results show how many tests passed, failed, and what percentage of the code is covered.',
    what:        'Automated test execution',
  },
  {
    key:         'security',
    label:       'Risks Scanned',
    icon:        '🔒',
    description: 'The generated application was scanned for security vulnerabilities including injection risks, weak cryptography, hardcoded secrets, and other common issues.',
    what:        'Automated security scanning',
  },
];

// ── Stage popup content ───────────────────────────────────────────────────────
function StagePopupContent({ stage, status, stageData, elapsed, onClose }) {
  const statusColor = {
    active: '#0969DA', done: '#1A7F37', error: '#CF222E',
    warn: '#9A6700', skip: '#57606A', idle: '#57606A',
  }[status] || '#57606A';

  const statusLabel = {
    active: '⟳ Running…', done: '✅ Complete', error: '❌ Error',
    warn: '⚠ Warning', skip: '— Skipped', idle: '· Waiting',
  }[status] || status;

  return (
    <div style={{ padding: '14px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1A1F2E' }}>
          {stage.icon} {stage.label}
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#57606A', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
      </div>
      <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: statusColor + '18', color: statusColor, marginBottom: 10 }}>
        {statusLabel}
      </div>
      <div style={{ fontSize: 12, color: '#57606A', lineHeight: 1.6, marginBottom: 10 }}>{stage.description}</div>
      <div style={{ fontSize: 11, fontFamily: 'monospace', background: '#F6F8FA', border: '1px solid #D0D7DE', borderRadius: 6, padding: '6px 10px', color: '#0969DA' }}>
        {stage.what}
      </div>
      {(stageData && Object.keys(stageData).length > 0 || elapsed) && (
        <div style={{ marginTop: 10, borderTop: '1px solid #D0D7DE', paddingTop: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#57606A', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Live Data</div>
          {elapsed && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
              <span style={{ color: '#57606A' }}>Duration</span>
              <span style={{ fontWeight: 700, color: '#1A7F37' }}>⏱ {elapsed}</span>
            </div>
          )}
          {stageData && Object.entries(stageData).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
              <span style={{ color: '#57606A' }}>{k}</span>
              <span style={{ fontWeight: 700, color: '#1A1F2E' }}>{String(v)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StagePill({ stage, status, stageData, elapsed, lane }) {
  const [anchorRect, setAnchorRect] = useState(null);
  const [showRationalize, setShowRationalize] = useState(false);
  const [showRequirements, setShowRequirements] = useState(false);
  const s = status || 'idle';

  const isActive = s === 'active';
  const isDone   = s === 'done' || s === 'warn';
  const isError  = s === 'error';
  const isSkip   = s === 'skip';

  const bg     = isActive ? '#DDF4FF' : isDone ? '#DAFBE1' : isError ? '#FFEBE9' : '#F6F8FA';
  const border = isActive ? '#0969DA' : isDone ? '#1A7F37' : isError ? '#CF222E' : '#D0D7DE';
  const color  = isActive ? '#0969DA' : isDone ? '#1A7F37' : isError ? '#CF222E' : '#57606A';

  const icon = isActive ? <span className="spin" style={{ display: 'inline-block' }}>⟳</span>
             : isDone   ? '✓'
             : isError  ? '✗'
             : isSkip   ? '—'
             : stage.icon;

  const handleClick = (e) => {
    e.stopPropagation();
    // Rationalize stage with scores → show full scoring modal
    if (stage.key === 'rationalize' && isDone && lane?.scores && Object.keys(lane.scores).length > 0) {
      setShowRationalize(true);
      return;
    }
    // Requirements stage when done → show requirements text modal
    if (stage.key === 'requirements' && isDone) {
      setShowRequirements(true);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setAnchorRect(prev => prev ? null : rect);
  };

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      <div
        onClick={handleClick}
        title={`Click for details: ${stage.label}`}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '5px 12px', borderRadius: 16,
          fontSize: 11, fontWeight: 600,
          border: `1.5px solid ${border}`,
          background: bg, color,
          cursor: 'pointer', whiteSpace: 'nowrap',
          transition: 'all 0.2s',
          boxShadow: isActive ? `0 0 0 3px ${border}33` : 'none',
          animation: isActive ? 'lane-stage-pulse 1.4s ease infinite' : 'none',
          userSelect: 'none',
        }}
      >
        <span>{icon}</span>
        <span>{stage.label}</span>
        {(isDone || isError) && elapsed && (
          <span style={{ fontSize: 9, opacity: 0.75, marginLeft: 2 }}>⏱{elapsed}</span>
        )}
      </div>

      {/* Full rationalization modal */}
      {showRationalize && lane && (
        <RationalizationModal lane={lane} onClose={() => setShowRationalize(false)} />
      )}

      {/* Requirements read-only modal */}
      {showRequirements && lane && (
        <RequirementsModal lane={lane} onClose={() => setShowRequirements(false)} />
      )}

      {/* Portal popup — always on top */}
      {anchorRect && (
        <Popup anchorRect={anchorRect} onClose={() => setAnchorRect(null)} width={290}>
          <StagePopupContent
            stage={stage} status={s} stageData={stageData}
            elapsed={elapsed} onClose={() => setAnchorRect(null)}
          />
        </Popup>
      )}
    </div>
  );
}

// ── Requirements read-only modal ──────────────────────────────────────────────
function RequirementsModal({ lane, onClose }) {
  const text = lane.requirementsText || '';

  // Simple markdown renderer — headings, bold, lists, tables
  const renderLine = (line, i) => {
    if (line.startsWith('### ')) return <h4 key={i} style={{ margin: '12px 0 4px', fontSize: 12, color: '#0969DA', fontWeight: 700 }}>{line.slice(4)}</h4>;
    if (line.startsWith('## '))  return <h3 key={i} style={{ margin: '14px 0 5px', fontSize: 13, color: '#1A1F2E', fontWeight: 800, borderBottom: '1px solid #EFF2F5', paddingBottom: 4 }}>{line.slice(3)}</h3>;
    if (line.startsWith('# '))   return <h2 key={i} style={{ margin: '16px 0 6px', fontSize: 15, color: '#1A1F2E', fontWeight: 800 }}>{line.slice(2)}</h2>;
    if (line.startsWith('- [ ] ') || line.startsWith('- [x] ')) {
      const done = line.startsWith('- [x]');
      return <div key={i} style={{ display: 'flex', gap: 6, fontSize: 11, marginBottom: 2, paddingLeft: 8 }}>
        <span style={{ color: done ? '#1A7F37' : '#57606A' }}>{done ? '☑' : '☐'}</span>
        <span style={{ color: '#1A1F2E' }}>{line.slice(6)}</span>
      </div>;
    }
    if (line.startsWith('- '))   return <div key={i} style={{ fontSize: 11, color: '#1A1F2E', paddingLeft: 12, marginBottom: 2 }}>• {line.slice(2)}</div>;
    if (line.startsWith('| '))   return <div key={i} style={{ fontSize: 10, fontFamily: 'monospace', color: '#57606A', paddingLeft: 4, marginBottom: 1, whiteSpace: 'pre' }}>{line}</div>;
    if (line.startsWith('---') || line.startsWith('===')) return <hr key={i} style={{ border: 'none', borderTop: '1px solid #D0D7DE', margin: '8px 0' }} />;
    if (!line.trim()) return <div key={i} style={{ height: 6 }} />;
    // Bold inline
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return <div key={i} style={{ fontSize: 11, color: '#1A1F2E', lineHeight: 1.6, marginBottom: 1 }}>
      {parts.map((p, j) => p.startsWith('**') && p.endsWith('**')
        ? <strong key={j}>{p.slice(2, -2)}</strong>
        : p)}
    </div>;
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 3000,
      background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        width: '100%', maxWidth: 760, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        animation: 'modal-in 0.18s ease',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #D0D7DE',
          background: '#F6F8FA', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 18 }}>📝</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: '#1A1F2E' }}>Requirements — {lane.name}</div>
            <div style={{ fontSize: 11, color: '#57606A', marginTop: 1 }}>
              {lane.lang} · {lane.fw} · {text ? `${text.split('\n').length} lines` : 'No content'}
            </div>
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
            background: '#DAFBE1', color: '#1A7F37' }}>Read-only</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer',
            color: '#57606A', fontSize: 18, lineHeight: 1, padding: 0, marginLeft: 4 }}>×</button>
        </div>

        {/* Content */}
        <div style={{ overflowY: 'auto', padding: '16px 24px', flex: 1 }}>
          {text
            ? text.split('\n').map((line, i) => renderLine(line, i))
            : <div style={{ color: '#8B949E', fontStyle: 'italic', fontSize: 12, textAlign: 'center', marginTop: 40 }}>
                Requirements not yet available — run the pipeline to generate them.
              </div>
          }
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 20px', borderTop: '1px solid #D0D7DE', background: '#F6F8FA',
          display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button onClick={onClose} style={{
            padding: '6px 16px', borderRadius: 6, fontSize: 12, fontWeight: 700,
            background: '#0969DA', color: '#fff', border: 'none', cursor: 'pointer',
          }}>Close</button>
        </div>
      </div>
    </div>
  );
}
function ScoreBar({ score }) {
  const pct   = Math.round((score / 10) * 100);
  const color = score >= 7 ? '#CF222E' : score >= 5 ? '#9A6700' : '#1A7F37';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 56, height: 7, background: '#EFF2F5', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.6s' }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color }}>{score?.toFixed(1)}</span>
    </div>
  );
}

// ── Lane row ──────────────────────────────────────────────────────────────────
export default function AppLanes({ lanes, laneTimes }) {
  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title-accent">⚡</span> Application Progress
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--txt2)', fontWeight: 400 }}>
          Click any stage for details
        </span>
      </div>

      <style>{`
        @keyframes lane-stage-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(9,105,218,0.35); }
          50%       { box-shadow: 0 0 0 6px rgba(9,105,218,0); }
        }
      `}</style>

      <div className="lanes-wrapper">
        {lanes.map(lane => {
          const dec      = lane.decision;
          const isActive = Object.values(lane.stages).includes('active');
          const isDone   = !isActive && Object.values(lane.stages).every(s => ['done','skip','warn'].includes(s));
          const laneClass = dec === 'Retire' ? 'lane-retired' : isActive ? 'lane-active' : isDone ? 'lane-done' : '';

          return (
            <div key={lane.name} className={`lane ${laneClass}`}>
              <div className="lane-header">
                <div>
                  <div className="lane-app-name">{lane.name}</div>
                  <div className="lane-meta">
                    {lane.lang} · {lane.fw} · {lane.loc?.toLocaleString()} lines of code
                    {lane.issues > 0 && (
                      <span style={{ color: 'var(--red)', marginLeft: 6 }}>⚠ {lane.issues} issues</span>
                    )}
                  </div>
                </div>

                {lane.score != null && <ScoreBar score={lane.score} />}

                <div className={`lane-decision-badge ${dec ? `badge-${dec.toLowerCase()}` : 'badge-pending'}`}>
                  {dec || 'Pending'}
                </div>
              </div>

              <div className="lane-stages">
                {STAGES.map((stage, i) => {
                  // Build live data for the popup from whatever the lane has
                  const stageData = {};
                  if (stage.key === 'rationalize' && lane.recommendation) {
                    stageData['Recommendation'] = lane.recommendation;
                    stageData['Risk Score']     = lane.score?.toFixed(1) + '/10';
                  }
                  if (stage.key === 'testing' && lane.testPassed != null) {
                    stageData['Passed']   = lane.testPassed;
                    stageData['Failed']   = lane.testFailed;
                    stageData['Coverage'] = lane.coverage + '%';
                  }
                  if (stage.key === 'security' && lane.secHigh != null) {
                    stageData['High Risk']   = lane.secHigh;
                    stageData['Medium Risk'] = lane.secMedium;
                    stageData['Low Risk']    = lane.secLow;
                  }

                  return (
                    <React.Fragment key={stage.key}>
                      <StagePill
                        stage={stage}
                        status={lane.stages[stage.key]}
                        stageData={stageData}
                        elapsed={laneTimes?.[`${lane.name}:${stage.key}`]}
                        lane={lane}
                      />
                      {i < STAGES.length - 1 && (
                        <span style={{ color: 'var(--border)', fontSize: 12 }}>›</span>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
